import {
  REMOVAL_BEYOND_SCOPE,
  REMOVAL_BEYOND_SCOPE_INCLUDED_WIDTH_M,
  computeRemovalBeyondScopeQuantity,
  computeZakopanyM2FromWidth,
  getExcavationSurfaceBeyondScopeRows,
} from '../constants/excavation-material.constants';
import { ORG_USER_UNIT_PRICE_META } from '../constants/org-work-rates.util';
import {
  findWorkReportPriceListItem,
  isAmountModePriceListItem,
  type PriceListUnit,
  type WorkReportPriceListItem,
} from '../constants/work-report-price-list.constants';
import {
  WORK_REPORT_PAYMENT_STATUS,
  isWorkReportPaymentLockedForUser,
  normalizeWorkReportPaymentStatus,
} from '../constants/work-report.constants';
import {
  isBouraniWorkReportActivityName,
  isExcavationSurfaceWorkReportActivityName,
  isObnovaSurfaceWorkReportActivityName,
  isPokladkaHdpeMultiductWorkReportActivityName,
  isRozvozWorkReportActivityName,
  resolveWorkReportPokladkaHdpeLengthM,
  resolveWorkReportRouteLengthM,
  type WorkReportLineItemDetailInput,
  isZafukSekundarnichOkWorkReportRow,
} from './work-report-line-item-detail.util';
import {
  isDeepeningWorkReportActivityName,
  workReportDeepeningCm,
} from './work-report-deepening-display.util';
import {
  isUkonceniVRd,
  isUkonceniVRdSelectionValid,
  UKONCENI_V_RD_LABELS,
  UKONCENI_V_RD_VALUES,
  ukonceniVRdRequiresOtherText,
} from '../constants/object-workflow/ukonceni-v-rd.constants';

const PHASE_ASSIGNMENT_PARAM_KEYS: WorkReportEditableParamKey[] = [
  'lengthM',
  'widthM',
  'depthM',
  'deepeningCm',
];

export type WorkReportEditableParamKey =
  | 'lengthM'
  | 'quantity'
  | 'widthM'
  | 'depthM'
  | 'deepeningCm';

export interface WorkReportEditableField {
  key: WorkReportEditableParamKey;
  label: string;
  unitLabel: string;
}

export type WorkReportEditableValues = Partial<Record<WorkReportEditableParamKey, number>>;

export type WorkReportEditableSpecKey = 'ukonceniVRd' | 'ukonceniVRdOther';

export interface WorkReportEditableSpecField {
  key: WorkReportEditableSpecKey;
  label: string;
  kind: 'enum' | 'text';
  enumValues?: readonly string[];
  enumLabels?: Record<string, string>;
}

export type WorkReportEditableSpecValues = Partial<Record<WorkReportEditableSpecKey, string>>;

export interface WorkReportEntryEditContext {
  fields: WorkReportEditableField[];
  values: WorkReportEditableValues;
  specFields: WorkReportEditableSpecField[];
  specValues: WorkReportEditableSpecValues;
  priceListItem?: WorkReportPriceListItem;
  unit?: PriceListUnit;
  pricePerUnit: number;
  computedQuantity: number | null;
  computedPrice: number | null;
  supportsAutoPrice: boolean;
  /** Rozměry z mapy (1×2×4 m, délka povrchu, …) — mimo Nevykopaný/Vykopaný jen náhled. */
  phaseAssignmentParamsLocked: boolean;
}

export interface WorkReportEntryEditInput extends WorkReportLineItemDetailInput {
  price?: number | null;
  objectId?: string | null;
}

export interface BuildWorkReportEntryEditContextOptions {
  objectType?: string | null;
  objectStatus?: string | null;
  /** Admin může upravit i vyfakturovaný řádek a rozměry i po Finální. */
  isAdmin?: boolean;
  paymentStatus?: string | null;
}

export interface WorkReportPhaseAssignmentLockOptions {
  isAdmin?: boolean;
  paymentStatus?: string | null;
}

/** Činnosti, jejichž rozměry odpovídají „Zadání při změně fáze“ (např. bourání 1×2×4 m). */
export function isWorkReportPhaseAssignmentActivity(activityName: string | null | undefined): boolean {
  const name = (activityName ?? '').trim();
  if (!name) return false;
  return (
    isBouraniWorkReportActivityName(name) ||
    isRozvozWorkReportActivityName(name) ||
    isExcavationSurfaceWorkReportActivityName(name) ||
    isObnovaSurfaceWorkReportActivityName(name) ||
    isDeepeningWorkReportActivityName(name) ||
    isPokladkaHdpeMultiductWorkReportActivityName(name)
  );
}

/** True = rozměry z přechodu fáze už nesmí měnit (vyfakturováno, nebo mapa po Finální). */
export function shouldLockWorkReportPhaseAssignmentParams(
  input: WorkReportEntryEditInput,
  objectType: string | null | undefined,
  objectStatus: string | null | undefined,
  options?: WorkReportPhaseAssignmentLockOptions,
): boolean {
  if (options?.isAdmin) return false;
  if (isWorkReportPaymentLockedForUser(options?.paymentStatus, false)) {
    return true;
  }
  // Výkaz práce: nevyfakturovaný řádek lze upravit i po Finální (objekt za Vykopaný).
  if (!input.objectId?.trim()) return false;
  if (!isWorkReportPhaseAssignmentActivity(input.activityName)) return false;
  return false;
}

export function filterWorkReportEditableFieldsForPhase(
  fields: WorkReportEditableField[],
  input: WorkReportEntryEditInput,
  objectType: string | null | undefined,
  objectStatus: string | null | undefined,
  options?: WorkReportPhaseAssignmentLockOptions,
): WorkReportEditableField[] {
  if (!shouldLockWorkReportPhaseAssignmentParams(input, objectType, objectStatus, options)) {
    return fields;
  }
  return fields.filter((f) => !PHASE_ASSIGNMENT_PARAM_KEYS.includes(f.key));
}

/** Backend / API guard — vrátí chybovou zprávu, pokud PATCH nesmí měnit rozměry zadání. */
export function getWorkReportPhaseAssignmentParamsLockReason(
  input: WorkReportEntryEditInput,
  objectType: string | null | undefined,
  objectStatus: string | null | undefined,
  params: Partial<Record<WorkReportEditableParamKey, number | undefined>>,
  options?: WorkReportPhaseAssignmentLockOptions,
): string | null {
  const hasDimensionEdit = PHASE_ASSIGNMENT_PARAM_KEYS.some((k) => params[k] !== undefined);
  if (!hasDimensionEdit) return null;
  if (!shouldLockWorkReportPhaseAssignmentParams(input, objectType, objectStatus, options)) {
    return null;
  }
  if (isWorkReportPaymentLockedForUser(options?.paymentStatus, false)) {
    const s = normalizeWorkReportPaymentStatus(options?.paymentStatus);
    if (s === WORK_REPORT_PAYMENT_STATUS.PAID) {
      return 'Proplacený řádek výkazu nelze upravit.';
    }
    return 'Vyfakturovaný řádek výkazu nelze upravit.';
  }
  return 'Rozměry zadání při změně fáze lze upravit jen ve fázi Nevykopaný/Vykopaný na mapě.';
}

function parsePositiveNumber(raw: unknown): number | undefined {
  if (raw == null || raw === '') return undefined;
  const n = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : undefined;
}

function excavationSnapshot(meta: Record<string, unknown> | null | undefined): Record<string, unknown> | undefined {
  const snap = meta?.['excavationSnapshot'];
  if (snap && typeof snap === 'object' && !Array.isArray(snap)) {
    return snap as Record<string, unknown>;
  }
  return undefined;
}

/** Kód Nad rámec / Obnova z ceníku — pro dohledání šířky v `excavationSurfaces`. */
function beyondScopeCodeForActivity(input: WorkReportEntryEditInput): string | undefined {
  const name = (input.activityName ?? '').trim();
  if (!name) return undefined;
  if (isBouraniWorkReportActivityName(name)) return REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY;
  if (isRozvozWorkReportActivityName(name)) return REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA;
  const listItemCode =
    typeof input.metadata?.['listItemCode'] === 'string'
      ? input.metadata['listItemCode'].trim()
      : undefined;
  const item = findWorkReportPriceListItem(name, listItemCode);
  const cond = item?.condition1Value;
  if (cond?.type === 'removal') return cond.code;
  if (cond?.type === 'work_zakopany') return cond.code;
  if (cond?.type === 'restoration_zakopany') return cond.code;
  return undefined;
}

function readDimensionsFromExcavationSurfaces(
  input: WorkReportEntryEditInput,
  snap: Record<string, unknown> | undefined,
  preferredCode: string | undefined,
): { widthM?: number; lengthM?: number; depthM?: number } {
  const surfacesRaw = snap?.['excavationSurfaces'] ?? input.metadata?.['excavationSurfaces'];
  if (!Array.isArray(surfacesRaw)) return {};
  const rowLen = parsePositiveNumber(input.excavationLengthM);
  const rowMat =
    typeof input.excavationMaterial === 'string' ? input.excavationMaterial.trim() : '';
  const rowSub =
    typeof input.excavationMaterialSubtype === 'string'
      ? input.excavationMaterialSubtype.trim()
      : '';

  type Hit = { widthM: number; lengthM: number; score: number };
  let best: Hit | undefined;

  for (const raw of surfacesRaw) {
    if (!raw || typeof raw !== 'object') continue;
    const rec = raw as Record<string, unknown>;
    const lengthM = parsePositiveNumber(rec['lengthM']);
    if (lengthM == null || lengthM <= 0) continue;
    const beyondRows = getExcavationSurfaceBeyondScopeRows(rec);
    for (const beyond of beyondRows) {
      const code = beyond.code;
      const widthM = beyond.widthM;
      if (preferredCode && code !== preferredCode) continue;

      let score = 1;
      if (preferredCode && code === preferredCode) score += 4;
      const mat =
        typeof rec['excavationMaterial'] === 'string' ? rec['excavationMaterial'].trim() : '';
      const sub =
        typeof rec['excavationMaterialSubtype'] === 'string'
          ? rec['excavationMaterialSubtype'].trim()
          : '';
      if (rowMat && mat === rowMat) score += 2;
      if (rowSub && sub === rowSub) score += 1;
      if (rowLen != null && Math.abs(lengthM - rowLen) < 0.001) score += 2;

      if (!best || score > best.score) {
        best = { widthM, lengthM, score };
      }
    }
  }
  return best ? { widthM: best.widthM, lengthM: best.lengthM } : {};
}

function readSpecialRemovalDimensions(
  input: WorkReportEntryEditInput,
): { widthM?: number; lengthM?: number; depthM?: number } {
  const meta = input.metadata;
  const snap = meta ? excavationSnapshot(meta) : undefined;
  const rowsRaw = snap?.['specialRemovalRows'] ?? meta?.['specialRemovalRows'];
  if (Array.isArray(rowsRaw)) {
    for (const raw of rowsRaw) {
      if (!raw || typeof raw !== 'object') continue;
      const rec = raw as Record<string, unknown>;
      const code = typeof rec['code'] === 'string' ? rec['code'].trim() : '';
      if (!code) continue;
      if (isBouraniWorkReportActivityName(input.activityName ?? '') && code === REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY) {
        return {
          widthM: parsePositiveNumber(rec['widthM']),
          lengthM: parsePositiveNumber(rec['lengthM']),
          depthM: parsePositiveNumber(rec['depthM']),
        };
      }
      if (isRozvozWorkReportActivityName(input.activityName ?? '') && code === REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA) {
        return {
          widthM: parsePositiveNumber(rec['widthM']),
          lengthM: parsePositiveNumber(rec['lengthM']),
          depthM: parsePositiveNumber(rec['depthM']),
        };
      }
    }
  }
  const legacyCode =
    (typeof meta?.['removalBeyondScope'] === 'string' ? meta['removalBeyondScope'].trim() : '') ||
    (typeof snap?.['removalBeyondScope'] === 'string'
      ? (snap['removalBeyondScope'] as string).trim()
      : '');
  if (legacyCode) {
    return {
      widthM:
        parsePositiveNumber(meta?.['removalBeyondScopeWidthM']) ??
        parsePositiveNumber(snap?.['removalBeyondScopeWidthM']),
      lengthM:
        parsePositiveNumber(meta?.['removalBeyondScopeLengthM']) ??
        parsePositiveNumber(snap?.['removalBeyondScopeLengthM']) ??
        parsePositiveNumber(input.excavationLengthM),
      depthM:
        parsePositiveNumber(meta?.['removalBeyondScopeDepthM']) ??
        parsePositiveNumber(snap?.['removalBeyondScopeDepthM']),
    };
  }

  // Per-povrch Nad rámec (asfalt / finální dlažba…) — šířka jen v excavationSurfaces[].
  const preferredCode = beyondScopeCodeForActivity(input);
  const fromSurfaces = readDimensionsFromExcavationSurfaces(input, snap, preferredCode);
  if (fromSurfaces.widthM != null) return fromSurfaces;

  const zakopanyWidth =
    parsePositiveNumber(meta?.['workBeyondScopeZakopanyWidthM']) ??
    parsePositiveNumber(meta?.['restorationZakopanyWidthM']) ??
    parsePositiveNumber(snap?.['workBeyondScopeZakopanyWidthM']) ??
    parsePositiveNumber(snap?.['restorationZakopanyWidthM']);
  if (zakopanyWidth != null) {
    return {
      widthM: zakopanyWidth,
      lengthM:
        parsePositiveNumber(meta?.['zakopanyLengthM']) ??
        parsePositiveNumber(snap?.['zakopanyLengthM']) ??
        parsePositiveNumber(meta?.['removalBeyondScopeLengthM']) ??
        parsePositiveNumber(snap?.['removalBeyondScopeLengthM']) ??
        parsePositiveNumber(input.excavationLengthM),
    };
  }

  const zakSurfaces = snap?.['zakopanySurfaces'] ?? meta?.['zakopanySurfaces'];
  if (Array.isArray(zakSurfaces)) {
    for (const raw of zakSurfaces) {
      if (!raw || typeof raw !== 'object') continue;
      const rec = raw as Record<string, unknown>;
      const code = typeof rec['code'] === 'string' ? rec['code'].trim() : '';
      if (preferredCode && code && code !== preferredCode) continue;
      const widthM = parsePositiveNumber(rec['widthM']);
      const lengthM =
        parsePositiveNumber(rec['lengthM']) ?? parsePositiveNumber(input.excavationLengthM);
      if (widthM != null && lengthM != null) return { widthM, lengthM };
    }
  }

  // Ceník nový (volume mode): rozměry z obnovaSurfaces / excavationSurfaces pro surface_bucket položky
  const listItemCode =
    typeof meta?.['listItemCode'] === 'string' ? meta['listItemCode'].trim() : undefined;
  const item = findWorkReportPriceListItem((input.activityName ?? '').trim(), listItemCode);
  const bucketCond = item?.condition1Value?.type === 'surface_bucket' ? item.condition1Value : undefined;
  if (bucketCond) {
    const obnovaRaw = snap?.['obnovaSurfaces'] ?? meta?.['obnovaSurfaces'];
    if (bucketCond.phaseScope === 'obnova' && Array.isArray(obnovaRaw)) {
      for (const raw of obnovaRaw) {
        if (!raw || typeof raw !== 'object') continue;
        const rec = raw as Record<string, unknown>;
        if (String(rec['surfaceBucket'] ?? '').trim() !== bucketCond.bucket) continue;
        const lengthM = parsePositiveNumber(rec['lengthM']);
        const widthM = parsePositiveNumber(rec['widthM']);
        const depthM = parsePositiveNumber(rec['depthM']);
        if (lengthM != null && widthM != null) return { widthM, lengthM, depthM };
      }
    }
    const excRaw = snap?.['excavationSurfaces'] ?? meta?.['excavationSurfaces'];
    if (Array.isArray(excRaw)) {
      for (const raw of excRaw) {
        if (!raw || typeof raw !== 'object') continue;
        const rec = raw as Record<string, unknown>;
        const segBucket = String(rec['surfaceBucket'] ?? '').trim();
        if (bucketCond.bucket === 'VOLNY_TEREN' && item?.unit === 'm3') {
          const depthM = parsePositiveNumber(rec['depthM']);
          const vLen = parsePositiveNumber(rec['volumeLengthM']) ?? parsePositiveNumber(rec['lengthM']);
          const vWid = parsePositiveNumber(rec['volumeWidthM']) ?? parsePositiveNumber(rec['widthM']);
          if (segBucket === 'VOLNY_TEREN' || depthM != null || parsePositiveNumber(rec['volumeLengthM']) != null) {
            if (vLen != null && vWid != null) return { widthM: vWid, lengthM: vLen, depthM };
          }
        } else if (segBucket === bucketCond.bucket) {
          const sLen = parsePositiveNumber(rec['surfaceLengthM']) ?? parsePositiveNumber(rec['lengthM']);
          const sWid = parsePositiveNumber(rec['surfaceWidthM']) ?? parsePositiveNumber(rec['widthM']);
          if (sLen != null && sWid != null) return { widthM: sWid, lengthM: sLen };
        }
      }
    }
  }

  // Root šířka bez legacy kódu (některé naceněné řádky mají jen WidthM + délku).
  const rootWidth =
    parsePositiveNumber(meta?.['removalBeyondScopeWidthM']) ??
    parsePositiveNumber(snap?.['removalBeyondScopeWidthM']);
  if (rootWidth != null) {
    return {
      widthM: rootWidth,
      lengthM:
        parsePositiveNumber(meta?.['removalBeyondScopeLengthM']) ??
        parsePositiveNumber(snap?.['removalBeyondScopeLengthM']) ??
        parsePositiveNumber(input.excavationLengthM),
      depthM:
        parsePositiveNumber(meta?.['removalBeyondScopeDepthM']) ??
        parsePositiveNumber(snap?.['removalBeyondScopeDepthM']),
    };
  }

  return {};
}

/**
 * Dopočet šířky z ceny u m² položek (bez odečtu 0,35 m — ceník nový).
 */
function deriveM2WidthFromPrice(
  input: WorkReportEntryEditInput,
  lengthM: number,
  pricePerUnit: number,
): number | undefined {
  if (lengthM <= 0 || !(pricePerUnit > 0)) return undefined;
  const price = parsePositiveNumber(input.price);
  if (price == null || price <= 0) return undefined;
  const qty = price / pricePerUnit;
  if (!(Number.isFinite(qty) && qty > 0)) return undefined;
  const widthM = qty / lengthM;
  return Number.isFinite(widthM) && widthM > 0 ? Math.round(widthM * 1000) / 1000 : undefined;
}

/**
 * Když u m² Nad rámec chybí uložená šířka, dopočti ji z ceny a délky:
 * qty = (šířka − 0,35) × délka → šířka = qty/délka + 0,35.
 */
function deriveBeyondScopeWidthFromPrice(
  input: WorkReportEntryEditInput,
  lengthM: number | undefined,
  pricePerUnit: number,
): number | undefined {
  if (lengthM == null || lengthM <= 0 || !(pricePerUnit > 0)) return undefined;
  const price = parsePositiveNumber(input.price);
  if (price == null || price <= 0) return undefined;
  const qty = price / pricePerUnit;
  if (!(Number.isFinite(qty) && qty > 0)) return undefined;
  const widthM = qty / lengthM + REMOVAL_BEYOND_SCOPE_INCLUDED_WIDTH_M;
  return Number.isFinite(widthM) && widthM > 0 ? Math.round(widthM * 1000) / 1000 : undefined;
}

function readDeepeningCm(input: WorkReportEntryEditInput): number | undefined {
  const snap = input.metadata ? excavationSnapshot(input.metadata) : undefined;
  const surfaces = snap?.['excavationSurfaces'];
  const legacy = snap?.['excavationDeepeningCm'];
  const cm = workReportDeepeningCm(surfaces, legacy);
  return cm != null && cm > 0 ? cm : undefined;
}

function readQuantityFromEntry(input: WorkReportEntryEditInput, pricePerUnit: number): number | undefined {
  const meta = input.metadata;
  if (meta && typeof meta === 'object') {
    const fromMeta = parsePositiveNumber(meta['quantity']);
    if (fromMeta != null) return fromMeta;
    const pokladka = parsePositiveNumber(meta['pokladkaHdpeLengthM']);
    if (pokladka != null) return pokladka;
  }
  // Abs — srážky (Půjčovna DEK) mají zápornou cenu, UI zadává kladnou Kč/množství.
  const priceRaw = typeof input.price === 'number' ? input.price : Number(input.price);
  if (Number.isFinite(priceRaw) && priceRaw !== 0 && pricePerUnit > 0) {
    const q = Math.abs(priceRaw) / pricePerUnit;
    return Number.isFinite(q) && q > 0 ? Math.round(q * 1000) / 1000 : undefined;
  }
  return undefined;
}

/** Určí editovatelná pole podle typu položky ceníku / názvu činnosti. */
export function resolveWorkReportEditableFields(
  input: WorkReportEntryEditInput,
): WorkReportEditableField[] {
  const name = (input.activityName ?? '').trim();
  if (!name) return [];

  const listItemCode =
    typeof input.metadata?.['listItemCode'] === 'string'
      ? input.metadata['listItemCode'].trim()
      : undefined;
  const item = findWorkReportPriceListItem(name, listItemCode);

  if (isPokladkaHdpeMultiductWorkReportActivityName(name)) {
    return [{ key: 'lengthM', label: 'Délka materiálu', unitLabel: 'm' }];
  }
  if (isDeepeningWorkReportActivityName(name)) {
    return [
      { key: 'lengthM', label: 'Délka', unitLabel: 'm' },
      { key: 'deepeningCm', label: 'Hloubka prohloubení', unitLabel: 'cm' },
    ];
  }
  if (isBouraniWorkReportActivityName(name)) {
    return [
      { key: 'widthM', label: 'Šířka', unitLabel: 'm' },
      { key: 'lengthM', label: 'Délka', unitLabel: 'm' },
      { key: 'depthM', label: 'Hloubka', unitLabel: 'm' },
    ];
  }
  if (isRozvozWorkReportActivityName(name)) {
    if (item?.unit === 'm3') {
      return [
        { key: 'widthM', label: 'Šířka', unitLabel: 'm' },
        { key: 'lengthM', label: 'Délka', unitLabel: 'm' },
        { key: 'depthM', label: 'Hloubka', unitLabel: 'm' },
      ];
    }
    return [
      { key: 'widthM', label: 'Šířka', unitLabel: 'm' },
      { key: 'lengthM', label: 'Délka', unitLabel: 'm' },
    ];
  }
  if (isExcavationSurfaceWorkReportActivityName(name) || isObnovaSurfaceWorkReportActivityName(name)) {
    const unit = item?.unit;
    if (unit === 'm3') {
      return [
        { key: 'widthM', label: 'Šířka', unitLabel: 'm' },
        { key: 'lengthM', label: 'Délka', unitLabel: 'm' },
        { key: 'depthM', label: 'Hloubka', unitLabel: 'm' },
      ];
    }
    if (unit === 'm2') {
      return [
        { key: 'widthM', label: 'Šířka', unitLabel: 'm' },
        { key: 'lengthM', label: 'Délka', unitLabel: 'm' },
      ];
    }
    return [{ key: 'lengthM', label: 'Délka povrchu', unitLabel: 'm' }];
  }

  if (!item) return [];

  if (isAmountModePriceListItem(item)) {
    return [{ key: 'quantity', label: 'Částka', unitLabel: 'Kč' }];
  }

  switch (item.unit) {
    case 'ks':
      return [{ key: 'quantity', label: 'Počet', unitLabel: 'ks' }];
    case 'm':
      return [{ key: 'lengthM', label: 'Délka', unitLabel: 'm' }];
    case 'm2':
      return [
        { key: 'widthM', label: 'Šířka', unitLabel: 'm' },
        { key: 'lengthM', label: 'Délka', unitLabel: 'm' },
      ];
    case 'm3':
      return [
        { key: 'widthM', label: 'Šířka', unitLabel: 'm' },
        { key: 'lengthM', label: 'Délka', unitLabel: 'm' },
        { key: 'depthM', label: 'Hloubka', unitLabel: 'm' },
      ];
    case 'h':
    case '1000_kg':
      return [{ key: 'quantity', label: 'Množství', unitLabel: item.unit }];
    default:
      return [];
  }
}

/** Enum/text spec pole pro záfuk sekundárních OK (ukončení v RD). */
export function resolveWorkReportEditableSpecFields(
  input: WorkReportEntryEditInput,
): WorkReportEditableSpecField[] {
  if (!isZafukSekundarnichOkWorkReportRow(input)) return [];
  return [
    {
      key: 'ukonceniVRd',
      label: 'Ukončení v RD',
      kind: 'enum',
      enumValues: UKONCENI_V_RD_VALUES,
      enumLabels: UKONCENI_V_RD_LABELS,
    },
    {
      key: 'ukonceniVRdOther',
      label: 'Ukončení v RD — upřesnění',
      kind: 'text',
    },
  ];
}

function readWorkReportSpecString(
  input: WorkReportEntryEditInput,
  key: WorkReportEditableSpecKey,
): string | undefined {
  const meta = input.metadata;
  if (meta && typeof meta === 'object') {
    const raw = meta[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
  }
  return undefined;
}

/** Načte hodnoty spec polí z metadata řádku výkazu. */
export function readWorkReportEditableSpecValues(
  input: WorkReportEntryEditInput,
  fields: WorkReportEditableSpecField[],
): WorkReportEditableSpecValues {
  const values: WorkReportEditableSpecValues = {};
  for (const field of fields) {
    const v = readWorkReportSpecString(input, field.key);
    if (v != null) values[field.key] = v;
  }
  return values;
}

/** Viditelnost volného textu při ukonceniVRd = JINE. */
export function shouldShowWorkReportUkonceniVRdOtherField(
  specValues: WorkReportEditableSpecValues,
): boolean {
  return ukonceniVRdRequiresOtherText(specValues.ukonceniVRd);
}

export function validateWorkReportEditableSpecValues(
  specValues: WorkReportEditableSpecValues,
  fields: WorkReportEditableSpecField[],
): string | null {
  if (fields.length === 0) return null;
  const uk = specValues.ukonceniVRd;
  if (!isUkonceniVRdSelectionValid(uk, specValues.ukonceniVRdOther)) {
    if (!isUkonceniVRd(uk)) return 'Vyberte ukončení v RD.';
    if (ukonceniVRdRequiresOtherText(uk)) return 'Při volbě „Jiné“ vyplňte upřesnění ukončení v RD.';
  }
  return null;
}

/** Načte aktuální hodnoty editovatelných polí z řádku výkazu. */
export function readWorkReportEditableValues(
  input: WorkReportEntryEditInput,
  fields: WorkReportEditableField[],
  pricePerUnit = 0,
): WorkReportEditableValues {
  const values: WorkReportEditableValues = {};
  const removal = readSpecialRemovalDimensions(input);

  for (const field of fields) {
    switch (field.key) {
      case 'lengthM':
        values.lengthM =
          resolveWorkReportPokladkaHdpeLengthM(input) ??
          (removal.widthM != null && removal.lengthM != null ? removal.lengthM : undefined) ??
          resolveWorkReportRouteLengthM(input) ??
          removal.lengthM;
        break;
      case 'quantity':
        values.quantity = readQuantityFromEntry(input, pricePerUnit);
        break;
      case 'widthM':
        values.widthM = removal.widthM;
        break;
      case 'depthM':
        values.depthM = removal.depthM;
        break;
      case 'deepeningCm':
        values.deepeningCm = readDeepeningCm(input);
        break;
      default:
        break;
    }
  }

  // Už naceněné m²: šířka často chybí — dopočet z ceny (nový ceník vs legacy nad rámec).
  if (
    fields.some((f) => f.key === 'widthM') &&
    values.widthM == null &&
    values.lengthM != null &&
    pricePerUnit > 0
  ) {
    const listItemCode =
      typeof input.metadata?.['listItemCode'] === 'string'
        ? input.metadata['listItemCode'].trim()
        : undefined;
    const priceListItem = findWorkReportPriceListItem((input.activityName ?? '').trim(), listItemCode);
    const derived =
      priceListItem?.condition1Type === 'surface_bucket' && priceListItem.unit === 'm2'
        ? deriveM2WidthFromPrice(input, values.lengthM, pricePerUnit)
        : deriveBeyondScopeWidthFromPrice(input, values.lengthM, pricePerUnit);
    if (derived != null) values.widthM = derived;
  }

  return values;
}

function removalCodeForActivity(name: string): string | undefined {
  if (isBouraniWorkReportActivityName(name)) return REMOVAL_BEYOND_SCOPE.BOURANI_BETON_ZAKLADY;
  if (isRozvozWorkReportActivityName(name)) return REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA;
  return undefined;
}

/** Vypočítá fakturované množství z editovatelných hodnot. */
export function computeWorkReportQuantityFromValues(
  input: WorkReportEntryEditInput,
  values: WorkReportEditableValues,
  item?: WorkReportPriceListItem,
): number | null {
  const name = (input.activityName ?? '').trim();
  if (!name) return null;

  const lengthM = parsePositiveNumber(values.lengthM);
  const quantity = parsePositiveNumber(values.quantity);
  const widthM = parsePositiveNumber(values.widthM);
  const depthM = parsePositiveNumber(values.depthM);
  const deepeningCm = parsePositiveNumber(values.deepeningCm);

  if (isPokladkaHdpeMultiductWorkReportActivityName(name)) {
    return lengthM != null && lengthM > 0 ? lengthM : null;
  }
  if (isDeepeningWorkReportActivityName(name)) {
    if (lengthM == null || lengthM <= 0 || deepeningCm == null || deepeningCm <= 0) return null;
    return Math.round(lengthM * (deepeningCm / 10) * 1000) / 1000;
  }

  const removalCode = removalCodeForActivity(name);
  if (removalCode && widthM != null && lengthM != null && widthM > 0 && lengthM > 0) {
    if (removalCode === REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA && item?.unit === 'm3') {
      if (depthM == null || depthM <= 0) return null;
      return Math.round(widthM * lengthM * depthM * 1000) / 1000;
    }
    const q = computeRemovalBeyondScopeQuantity(
      removalCode as (typeof REMOVAL_BEYOND_SCOPE)[keyof typeof REMOVAL_BEYOND_SCOPE],
      widthM,
      lengthM,
      depthM,
    );
    return q > 0 ? q : null;
  }

  if (isExcavationSurfaceWorkReportActivityName(name) || isObnovaSurfaceWorkReportActivityName(name)) {
    if (!item) return lengthM != null && lengthM >= 0 ? lengthM : null;
    if (item.unit === 'm2' && widthM != null && lengthM != null && widthM > 0 && lengthM > 0) {
      return Math.round(widthM * lengthM * 1000) / 1000;
    }
    if (item.unit === 'm3' && widthM != null && lengthM != null && depthM != null) {
      if (widthM <= 0 || lengthM <= 0 || depthM <= 0) return null;
      return Math.round(widthM * lengthM * depthM * 1000) / 1000;
    }
    return lengthM != null && lengthM >= 0 ? lengthM : null;
  }

  if (!item) return null;

  switch (item.unit) {
    case 'ks':
    case 'h':
    case '1000_kg':
      return quantity != null && quantity > 0 ? quantity : null;
    case 'm':
      return lengthM != null && lengthM > 0 ? lengthM : null;
    case 'm2': {
      if (widthM == null || lengthM == null || widthM <= 0 || lengthM <= 0) return null;
      const cond = item.condition1Value;
      if (cond?.type === 'work_zakopany' || cond?.type === 'restoration_zakopany') {
        const m2 = computeZakopanyM2FromWidth(widthM, lengthM);
        return m2 > 0 ? m2 : null;
      }
      const code =
        cond?.type === 'removal'
          ? cond.code
          : REMOVAL_BEYOND_SCOPE.ROZVOZ_ZEMINA;
      const q = computeRemovalBeyondScopeQuantity(
        code as (typeof REMOVAL_BEYOND_SCOPE)[keyof typeof REMOVAL_BEYOND_SCOPE],
        widthM,
        lengthM,
      );
      return q > 0 ? q : null;
    }
    case 'm3': {
      if (widthM == null || lengthM == null || depthM == null) return null;
      if (widthM <= 0 || lengthM <= 0 || depthM <= 0) return null;
      return Math.round(widthM * lengthM * depthM * 1000) / 1000;
    }
    default:
      return null;
  }
}

export function roundWorkReportMoney(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Vypočítá cenu z množství a jednotkové ceny (volitelně srážka / přímá částka). */
export function computeWorkReportPriceFromQuantity(
  quantity: number | null,
  pricePerUnit: number,
  item?: Pick<WorkReportPriceListItem, 'quantityMode' | 'alwaysNegative'> | null,
): number | null {
  if (quantity == null || !Number.isFinite(quantity) || quantity === 0) return null;
  if (!Number.isFinite(pricePerUnit) || pricePerUnit < 0) return null;
  // Amount / srážka: abs(vstup); běžné množství musí zůstat kladné.
  const allowSigned =
    item?.quantityMode === 'amount' || item?.alwaysNegative === true;
  const absQty = allowSigned ? Math.abs(quantity) : quantity;
  if (absQty <= 0) return null;
  const raw =
    item?.quantityMode === 'amount'
      ? roundWorkReportMoney(absQty)
      : roundWorkReportMoney(absQty * pricePerUnit);
  if (raw <= 0) return null;
  return item?.alwaysNegative ? -Math.abs(raw) : raw;
}

/** Kompletní kontext pro UI úpravy řádku výkazu. */
export function buildWorkReportEntryEditContext(
  input: WorkReportEntryEditInput,
  pricePerUnit: number,
  options?: BuildWorkReportEntryEditContextOptions,
): WorkReportEntryEditContext {
  const name = (input.activityName ?? '').trim();
  const listItemCode =
    typeof input.metadata?.['listItemCode'] === 'string'
      ? input.metadata['listItemCode'].trim()
      : undefined;
  const priceListItem = name ? findWorkReportPriceListItem(name, listItemCode) : undefined;
  const rawFields = resolveWorkReportEditableFields(input);
  const lockOptions: WorkReportPhaseAssignmentLockOptions = {
    isAdmin: options?.isAdmin,
    paymentStatus: options?.paymentStatus,
  };
  const fields = filterWorkReportEditableFieldsForPhase(
    rawFields,
    input,
    options?.objectType,
    options?.objectStatus,
    lockOptions,
  );
  const phaseAssignmentParamsLocked = shouldLockWorkReportPhaseAssignmentParams(
    input,
    options?.objectType,
    options?.objectStatus,
    lockOptions,
  );
  const values = readWorkReportEditableValues(input, fields, pricePerUnit);
  const specFields = resolveWorkReportEditableSpecFields(input);
  const specValues = readWorkReportEditableSpecValues(input, specFields);
  const computedQuantity = computeWorkReportQuantityFromValues(input, values, priceListItem);
  const computedPrice = computeWorkReportPriceFromQuantity(
    computedQuantity,
    pricePerUnit,
    priceListItem,
  );
  return {
    fields,
    values,
    specFields,
    specValues,
    priceListItem,
    unit: priceListItem?.unit,
    pricePerUnit,
    computedQuantity,
    computedPrice,
    supportsAutoPrice: fields.length > 0 && pricePerUnit > 0,
    phaseAssignmentParamsLocked,
  };
}

export interface WorkReportEntryUpdatePayload {
  activityName?: string;
  description?: string;
  price?: number;
  lengthM?: number;
  quantity?: number;
  widthM?: number;
  depthM?: number;
  deepeningCm?: number;
  ukonceniVRd?: string;
  ukonceniVRdOther?: string;
  /** ISO — admin úprava data/času výkazu. */
  createdAt?: string;
}

/** `orgUserUnitPrice` z řádku, včetně 0. Chybějící sazba vrací null (katalog / polygon). */
function storedWorkerUnitPrice(
  metadata: Record<string, unknown> | null | undefined,
): number | null {
  if (!metadata) return null;
  const raw = metadata[ORG_USER_UNIT_PRICE_META];
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : NaN;
  if (!Number.isFinite(n) || n < 0) return null;
  return n;
}

/** Sestaví PATCH payload z formulářových hodnot (bez přímé ceny, pokud lze dopočítat). */
export function buildWorkReportEntryUpdatePayload(
  input: WorkReportEntryEditInput,
  formValues: WorkReportEditableValues,
  options?: {
    priceOverride?: number;
    activityName?: string;
    description?: string;
    pricePerUnit?: number;
    objectType?: string | null;
    objectStatus?: string | null;
    specValues?: WorkReportEditableSpecValues;
  },
): WorkReportEntryUpdatePayload {
  const payload: WorkReportEntryUpdatePayload = {};
  if (options?.activityName?.trim()) payload.activityName = options.activityName.trim();
  if (options?.description !== undefined) payload.description = options.description?.trim() || undefined;

  const fields = filterWorkReportEditableFieldsForPhase(
    resolveWorkReportEditableFields(input),
    input,
    options?.objectType,
    options?.objectStatus,
  );
  for (const field of fields) {
    const v = parsePositiveNumber(formValues[field.key]);
    if (v == null) continue;
    payload[field.key] = v;
  }

  const specFields = resolveWorkReportEditableSpecFields(input);
  const specValues = options?.specValues ?? {};
  for (const field of specFields) {
    if (field.key === 'ukonceniVRdOther' && !shouldShowWorkReportUkonceniVRdOtherField(specValues)) {
      continue;
    }
    const raw = specValues[field.key];
    if (raw == null || raw.trim() === '') continue;
    if (field.key === 'ukonceniVRd' && !isUkonceniVRd(raw)) continue;
    payload[field.key] = raw.trim();
  }

  const listItemCode =
    typeof input.metadata?.['listItemCode'] === 'string'
      ? input.metadata['listItemCode'].trim()
      : undefined;
  const priceListItem = findWorkReportPriceListItem((input.activityName ?? '').trim(), listItemCode);
  const storedWorkerUnit = storedWorkerUnitPrice(input.metadata);
  const pricePerUnit =
    storedWorkerUnit != null ? storedWorkerUnit : options?.pricePerUnit ?? priceListItem?.pricePerUnit ?? 0;
  const recomputed = computeWorkReportPriceFromQuantity(
    computeWorkReportQuantityFromValues(input, formValues, priceListItem),
    pricePerUnit,
    priceListItem,
  );
  if (options?.priceOverride != null && Number.isFinite(options.priceOverride)) {
    payload.price = roundWorkReportMoney(options.priceOverride);
  } else if (recomputed != null) {
    payload.price = recomputed;
  } else if (storedWorkerUnit === 0) {
    payload.price = 0;
  }

  return payload;
}
